/**
 * Gift card types for the partner-facing Gift cards page.
 *
 * Wire shapes come from the generated admin schema types rather than being restated here. The
 * hand-written versions these replaced had drifted in both directions — `rows`/`count` declared
 * non-null when the schema says otherwise (which is how a malformed page reached the datagrid as
 * a TypeError), and `amount`/`number`/`createdAt` declared nullable when they are not.
 *
 * Amounts are in cents, in the card's transaction currency — pair every one with
 * `transactionCurrencyCode`. `amount` is not the raw column it looks like:
 * `Giftcard.priceFields = ['amount']` makes reware-api replace the field resolver
 * (lib/graphql/util.js), returning `amount_transaction` whenever the card carries a currency
 * exchange and the USD base only when it does not.
 *
 * The `amount` *filter* is the exception — `filtersToWhere` compares it against the raw column
 * without running the resolver, so filtering is USD-based even where the displayed value is not.
 * That asymmetry lives in the API, not here.
 */
import { z } from 'zod';

import type { RewareAdminTypes } from '@troverecommerce/shared-reware-api';

import type { ConstantOption } from '@src/components/indexPage';

export type { ConstantOption };

/**
 * `deliveryAddress` is a JSON scalar on both parents, selected without a sub-selection, so the
 * schema tells us nothing about its contents and the generated type is opaque. Parsed rather than
 * asserted: an interface here would be a guess the compiler then treats as fact.
 *
 * That guess has a cost. If the shape differs — keys absent rather than null, or the scalar
 * arriving as a JSON string — an asserted type still reads as truthy, so `CustomerSection` renders
 * three em dashes and the Resend button disables itself with "No email address on the associated
 * order or trade-in". The operator is told a fact about the customer's data that is really a
 * parsing failure, which sends them to fix the wrong thing.
 *
 * Only the three fields actually read are declared. Everything is optional because nothing
 * guarantees otherwise. Unknown keys (the shipping half of the address) are dropped rather than
 * rejected: a parsed value that matches its declared type exactly is worth more here than
 * carrying fields no caller reads.
 */
export const deliveryAddressSchema = z.object({
  name: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
});

export type DeliveryAddress = z.infer<typeof deliveryAddressSchema>;

/**
 * Narrow an unparsed `deliveryAddress` to the fields the page reads.
 *
 * Returns null when the value is absent or not an object, which callers must distinguish from a
 * parsed address whose fields are empty — the first means "we could not read this", the second
 * means "there is nothing recorded".
 */
export const parseDeliveryAddress = (value: unknown): DeliveryAddress | null => {
  if (value == null || typeof value !== 'object') return null;
  const parsed = deliveryAddressSchema.safeParse(value);
  if (!parsed.success) {
    console.error('Gift card delivery address did not match the expected shape', {
      issues: parsed.error.issues,
    });
    return null;
  }
  return parsed.data;
};

/** A gift card as the admin API returns it. `number` is the masked last-3 on list queries. */
export type GiftcardRecord = RewareAdminTypes.GiftcardAdmin;

export type GiftcardStateTransition = RewareAdminTypes.StateTransitionsAdmin;

/** The page envelope as it arrives — every field nullable. See GiftcardsResult for the parsed form. */
export type GiftcardsPage = RewareAdminTypes.PagedGiftcardAdmin;

/**
 * What `fetchGiftcards` returns once the envelope has been validated: the narrowing the wire type
 * cannot express. Keeping the two distinct is what stops callers re-deriving the null handling.
 */
export interface GiftcardsResult {
  count: number;
  rows: GiftcardRecord[];
}

/**
 * Either parent a gift card can hang off. Both carry the `uuidPrefix` and `deliveryAddress` that
 * `associatedModel` reads; a card may have neither.
 */
export type GiftcardParent = RewareAdminTypes.OrderAdmin | RewareAdminTypes.TradeinTypeAdmin;

export interface FetchGiftcardsFilters {
  partnerId: number;
  page: number;
  /**
   * Must always be sent. The API defaults to 10, so omitting it silently
   * desyncs react-admin's page arithmetic from the server's and makes the tail
   * of the result set unreachable.
   */
  pageSize: number;
  state?: string;
  source?: string;
  reason?: string;
  giftcardNumber?: string;
  giftcardId?: number;
  orderNumber?: string;
  tradeinNumber?: string;
  /** Cents. The filter input collects dollars; the provider converts. */
  amount?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export interface GiftcardConstants {
  states: ConstantOption[];
  sources: ConstantOption[];
  reasons: ConstantOption[];
}
